import { stripHtmlToPlainText } from '../../../shared/html-text.js';
import {
  calcRentalDays,
  DEFAULT_PICKUP_TIME,
  DEFAULT_RETURN_TIME,
  inferRentalTypeFromDates,
  normalizeHalfDayPickupDate,
  normalizeRentalDateRange
} from './rental-dates.js';
import { isRentalTypeOffered, getRentalTypeLabelSimple } from './pricing.js';

const MONTHS_NL = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december'
];

function isYmd(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

export function f2rTodayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Verwijder verlopen datums uit sessionStorage (oude zoekperiode). */
export function f2rClearStaleSearchPeriodStorage() {
  try {
    const raw = sessionStorage.getItem('vsb_rental_period');
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || !isYmd(parsed.from_date) || parsed.from_date < f2rTodayYmd()) {
      sessionStorage.removeItem('vsb_rental_period');
    }
  } catch {
    try {
      sessionStorage.removeItem('vsb_rental_period');
    } catch {
      /* ignore */
    }
  }
}

function readUrlSearchPeriod() {
  try {
    const params = new URLSearchParams(window.location.search);
    const from = params.get('from_date') || '';
    const to = params.get('to_date') || '';
    if (!isYmd(from) || from < f2rTodayYmd()) return null;
    return {
      from_date: from,
      to_date: isYmd(to) && to >= from ? to : from
    };
  } catch {
    return null;
  }
}

function readSessionSearchPeriod() {
  try {
    const raw = sessionStorage.getItem('vsb_rental_period');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !isYmd(parsed.from_date) || parsed.from_date < f2rTodayYmd()) {
      sessionStorage.removeItem('vsb_rental_period');
      return null;
    }
    const to = isYmd(parsed.to_date) ? parsed.to_date : parsed.from_date;
    return {
      from_date: parsed.from_date,
      to_date: to >= parsed.from_date ? to : parsed.from_date
    };
  } catch {
    return null;
  }
}

export function f2rHasUrlSearchPeriod() {
  return readUrlSearchPeriod() !== null;
}

export function f2rReadSearchPeriod() {
  const url = readUrlSearchPeriod();
  if (url) return url;
  return readSessionSearchPeriod();
}

export function f2rFormatPeriodLabel(from, to) {
  if (!isYmd(from)) return '';
  const fmt = (ymd) => {
    const parts = ymd.split('-');
    const day = parseInt(parts[2], 10);
    const month = parseInt(parts[1], 10);
    const year = parts[0];
    const name = MONTHS_NL[month - 1] || parts[1];
    return `${day} ${name} ${year}`;
  };
  if (to && to !== from && isYmd(to)) return `${fmt(from)} t/m ${fmt(to)}`;
  return fmt(from);
}

function readPricesFromRoot(rootEl) {
  if (!rootEl?.getAttribute) return null;
  const prices = {
    halve_dag: Number(rootEl.getAttribute('data-price-half-day') || 0),
    dag: Number(rootEl.getAttribute('data-price-day') || 0),
    weekend: Number(rootEl.getAttribute('data-price-weekend') || 0),
    week: Number(rootEl.getAttribute('data-price-week') || 0)
  };
  return prices;
}

export function f2rBuildPrefillPeriodLabel(from, to, rentalType) {
  const dates = f2rFormatPeriodLabel(from, to);
  const typeLabel = rentalType ? getRentalTypeLabelSimple(rentalType) : '';
  if (typeLabel && typeLabel !== '-' && dates) {
    return `${typeLabel} · ${dates}`;
  }
  return dates;
}

function buildSearchPeriodFormState(from, to, prices = null) {
  const normalized = normalizeRentalDateRange(from, to || from);
  const pickup = normalized.pickupDate;
  const ret = normalized.returnDate || pickup;
  const pickupTime = DEFAULT_PICKUP_TIME;
  const returnTime = DEFAULT_RETURN_TIME;
  const rentalType = inferRentalTypeFromDates(
    pickup,
    ret,
    pickupTime,
    returnTime,
    prices,
    isRentalTypeOffered
  );
  let normalizedPickup = pickup;
  let normalizedRet = ret;
  if (rentalType === 'halve_dag') {
    normalizedPickup = normalizeHalfDayPickupDate(pickup);
    normalizedRet = normalizedPickup;
  }
  let days = 1;
  const calculated = calcRentalDays(pickup, ret, pickupTime, returnTime);
  if (calculated != null) days = calculated;
  if (rentalType === 'halve_dag') days = 0.5;
  else if (rentalType === 'dag') days = 1;
  return {
    rentalType,
    pickupDate: normalizedPickup,
    returnDate: normalizedRet,
    pickupTime,
    returnTime,
    rentalDays: days
  };
}

export function f2rBuildSearchPeriodRestore(period, prices = null) {
  if (!period || !isYmd(period.from_date) || period.from_date < f2rTodayYmd()) return null;
  const pickup = period.from_date;
  const ret = isYmd(period.to_date) ? period.to_date : pickup;
  const formState = buildSearchPeriodFormState(pickup, ret, prices);
  return {
    prefillPeriodLabel: stripHtmlToPlainText(
      f2rBuildPrefillPeriodLabel(pickup, ret, formState.rentalType)
    ),
    formState
  };
}

export function f2rMergeSearchPeriodRestore(draft) {
  const base = draft && typeof draft === 'object' ? draft : {};
  if (base.fromSearch) return base;
  const fs = base.formState || {};
  const period = f2rReadSearchPeriod();
  if (!period) return base;
  if (fs.pickupDate && fs.rentalType && !f2rHasUrlSearchPeriod()) return base;

  const built = f2rBuildSearchPeriodRestore(period, null);
  if (!built) return base;

  const out = JSON.parse(JSON.stringify(base));
  out.formState = Object.assign({}, fs, built.formState);
  out.fromSearch = true;
  out.prefillPeriodLabel = stripHtmlToPlainText(built.prefillPeriodLabel || '');
  return out;
}

export function mergeSearchPeriodFromMount(draft, rootEl) {
  const base = draft && typeof draft === 'object' ? draft : {};
  const fs = base.formState || {};
  const fromAttr = rootEl?.getAttribute('data-search-from') || '';
  const hasServerSearchPrefill = /^\d{4}-\d{2}-\d{2}$/.test(fromAttr);

  const urlPeriod = readUrlSearchPeriod();
  if (!hasServerSearchPrefill && !urlPeriod && fs.pickupDate && fs.rentalType) {
    return base;
  }

  let from = '';
  let to = '';
  if (urlPeriod) {
    from = urlPeriod.from_date;
    to = urlPeriod.to_date;
  } else if (hasServerSearchPrefill) {
    from = fromAttr;
    to = rootEl.getAttribute('data-search-to') || '';
    if (!isYmd(to)) to = '';
  } else if (rootEl) {
    from = rootEl.getAttribute('data-search-from') || '';
    to = rootEl.getAttribute('data-search-to') || '';
    if (!isYmd(from)) from = '';
    if (!isYmd(to)) to = '';
  }

  if (!from) {
    const sessionPeriod = readSessionSearchPeriod();
    if (sessionPeriod) {
      from = sessionPeriod.from_date;
      to = sessionPeriod.to_date;
    }
  }

  if (!from || !isYmd(from) || from < f2rTodayYmd()) return base;

  const ret = to || from;
  const prices = readPricesFromRoot(rootEl);
  const out = JSON.parse(JSON.stringify(base));
  out.formState = Object.assign({}, fs, buildSearchPeriodFormState(from, ret, prices));
  out.fromSearch = true;
  const label = (rootEl && rootEl.getAttribute('data-search-label')) || '';
  out.prefillPeriodLabel = stripHtmlToPlainText(
    label || f2rBuildPrefillPeriodLabel(from, ret, out.formState.rentalType)
  );
  return out;
}
